import { THEME } from '@enmon/commons';
import type { ThemeConfig } from 'antd/lib';

const theme = {
  color: THEME.color,
  typography: THEME.typography,
  spacing: THEME.spacing,
  font: THEME.font,
  fonts: {
    heading: {
      family: 'Poppins, sans-serif' as const,
      weight: 500 as const,
    },
    body: {
      family: 'Poppins, sans-serif' as const,
      weight: 400 as const,
    },
  },
  breakpoints: {
    xxl: 1600,
    xl: 1200,
    lg: 992,
    md: 768,
    sm: 576,
  },
};

const primaryThemeToken = {
  borderRadius: 2,
  fontFamily: theme.font.typography.bodyText.fontFamily,
  fontWeightStrong: 500,
  colorPrimary: theme.color.theme.primary,
  colorLink: theme.color.theme.primary,
  lineWidthFocus: 0,
} satisfies ThemeConfig['token'];

export const antdTheme = {
  token: primaryThemeToken,
  hashed: false,
  components: {
    Layout: {
      bodyBg: theme.color.semantic.background.base,
    },
    Alert: {
      colorInfoBg: '#d8e2e0',
      colorInfo: theme.color.theme.primary,
    },
    Button: {
      contentFontSizeSM: 12,
      defaultHoverBg: theme.color.semantic.primary.background.hoverWeak,
      colorPrimary: theme.color.theme.primary,
      colorPrimaryHover: theme.color.theme.light,
      colorPrimaryActive: theme.color.theme.tertiary,
      colorError: theme.color.status.error,
      colorTextDisabled: theme.color.type.disabled,
      colorText: theme.color.theme.primary,
      defaultBg: theme.color.grey.white,
      defaultBorderColor: theme.color.theme.primary,
      defaultActiveBg: theme.color.theme.lighter,
      colorBgContainerDisabled: theme.color.grey.grey4,
    },
    Menu: {
      itemHoverBg: theme.color.theme.lighterAlt,
      itemHoverColor: theme.color.theme.primary,
      itemActiveBg: theme.color.theme.lighterAlt,
      itemSelectedBg: theme.color.theme.lighter,
      colorPrimaryHover: theme.color.theme.primary,
      lineWidthFocus: 2,
    },
    Tooltip: {
      sizePopupArrow: 0,
      borderRadius: 8,
      fontSize: theme.font.size.xs,
      fontWeightStrong: 600,
      colorBgSpotlight: theme.color.grey.white,
      colorText: theme.color.type.primary,
      colorTextLightSolid: theme.color.type.primary,
      colorLink: theme.color.theme.primary,
      boxShadow: '0px 6.4px 14.4px 0px #00000021, 0px 1.2px 3.6px 0px #0000001a',
    },
    Select: {
      optionSelectedColor: theme.color.type.primary,
      optionSelectedBg: theme.color.theme.lighter,
      controlItemBgHover: theme.color.theme.lighterAlt,
      controlItemBgActiveHover: theme.color.grey.grey3,
    },
    TreeSelect: {
      controlItemBgHover: theme.color.theme.lighterAlt,
      controlItemBgActiveHover: theme.color.grey.grey3,
    },
    Dropdown: {
      controlItemBgHover: theme.color.theme.lighterAlt,
      controlItemBgActiveHover: theme.color.grey.grey3,
    },
    Typography: {
      fontFamily: theme.font.typography.bodyText.fontFamily,
      fontWeightStrong: 500,
    },
    Pagination: {
      lineWidthFocus: 2,
    },
    Table: {
      rowHoverBg: theme.color.grey.grey3,
      rowSelectedBg: theme.color.theme.lighterAlt,
      rowSelectedHoverBg: theme.color.theme.lighter,
    },
    Tour: {
      colorText: theme.color.semantic.primary.type.default,
      colorBgElevated: theme.color.semantic.primary.background.hover,
      borderRadius: 8,
      borderRadiusOuter: 8,
      borderRadiusLG: 8,
      boxShadowTertiary: '0px 0px 14px 2px #25252545, 0px 0px 4px 1px #3233324a',
      boxShadowSecondary: 'none',
    },
    Popover: {
      borderRadius: 8,
      borderRadiusOuter: 8,
      borderRadiusLG: 8,
    },
  },
} satisfies ThemeConfig;

export const { breakpoints } = theme;

export default theme;
